import { AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractComponent,
  AttendanceCopilotActionsService,
  AttendanceCopilotInsightService,
  CopilotActionsItem,
  CopilotActionsUiState,
  CopilotActionsView,
  CopilotInsightUiState,
  CopilotInsightView,
} from 'lib-trend-core';
import { combineLatest, map, Observable } from 'rxjs';
import { CopilotDrawerPane } from '../attendance-copilot-drawer/attendance-copilot-drawer.component';
import { badgeTone, riskKey } from '../attendance-copilot-insight/insight-tones.util';

const INSIGHT_KEYS = 'attendance.copilot.insight.';
const ACTIONS_KEYS = 'attendance.copilot.actions.';
const SUMMARY_KEYS = 'attendance.copilot.summary.';

export interface CopilotSummaryVm {
  insight: CopilotInsightUiState;
  actions: CopilotActionsUiState;
}

/**
 * The summary strip at the top of the Copilot tab (prototype D): one line for the insight, one
 * for the Actions and the next pending action, markable in place. Everything else opens in the
 * drawer. The state lives in the two services; this component only reads it.
 */
@Component({
  selector: 'attendance-copilot-summary',
  templateUrl: './attendance-copilot-summary.component.html',
  styleUrls: ['./attendance-copilot-summary.component.scss'],
  standalone: false,
})
export class AttendanceCopilotSummaryComponent extends AbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() attendanceClosed = false;
  @Output() readonly open = new EventEmitter<CopilotDrawerPane>();

  readonly vm$: Observable<CopilotSummaryVm> = combineLatest([
    this.insightService.select(),
    this.actionsService.select(),
  ]).pipe(map(([insight, actions]) => ({ insight, actions })));

  timezone = '';
  /** The strip's own width; wide until measured, so nothing is hidden before the first reading. */
  width = Number.POSITIVE_INFINITY;

  private observer: ResizeObserver | null = null;

  constructor(
    injector: Injector,
    private readonly insightService: AttendanceCopilotInsightService,
    private readonly actionsService: AttendanceCopilotActionsService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.timezone = this.getTimezoneCompanyCurrentUser();
  }

  // The panel is a quarter of the screen and resizes with the window and the side menu; the strip
  // measures itself so each row can keep one line (the modifiers are in the stylesheet).
  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) this.zone.run(() => (this.width = width));
    });
    this.observer.observe(this.host.nativeElement);
  }

  override ngOnDestroy(): void {
    this.observer?.disconnect();
    super.ngOnDestroy();
  }

  /** Whether the strip is at least this wide (the thresholds are the stylesheet's modifiers). */
  fits(px: number): boolean {
    return this.width >= px;
  }

  visible(vm: CopilotSummaryVm): boolean {
    return this.showInsight(vm.insight) || this.showActions(vm.actions);
  }

  /** The insight line shows while there is an insight to show, or the conversation is open and will have one (RF-34). */
  showInsight(state: CopilotInsightUiState): boolean {
    return !!state.view && (!this.closed(state.view) || !!state.view.insight);
  }

  /** The Actions lines show from the first generation on, or when the actions can be asked for (RF-33, spec M-2). */
  showActions(state: CopilotActionsUiState): boolean {
    const view = state.view;
    if (!view) return false;
    if (this.closed(view)) return this.hasActions(view);
    return view.state !== 'none' || view.canGenerate;
  }

  closed(view: CopilotInsightView | CopilotActionsView): boolean {
    return this.attendanceClosed || view.readOnly;
  }

  generating(state: CopilotInsightUiState): boolean {
    return state.view?.state === 'generating' && !this.closed(state.view);
  }

  insightFailed(state: CopilotInsightUiState): boolean {
    return !!state.view && state.view.state === 'failed' && !state.view.insight && !this.closed(state.view);
  }

  waiting(state: CopilotInsightUiState): boolean {
    return !!state.view && !state.view.insight && !this.generating(state) && !this.insightFailed(state) && !this.closed(state.view);
  }

  /** The button waits for a message after the insight, and for the end of a generation (RF-30). */
  canUpdate(state: CopilotInsightUiState): boolean {
    return state.newMessages > 0 && !this.generating(state) && !state.requesting;
  }

  /** The icon button updates an insight, or generates the first one after a failure (RF-30, RF-31). */
  canRequest(state: CopilotInsightUiState): boolean {
    if (state.view?.insight) return this.canUpdate(state);
    return this.insightFailed(state) && !state.requesting;
  }

  requestKey(state: CopilotInsightUiState): string {
    if (!state.view?.insight) return INSIGHT_KEYS + 'generate';
    return INSIGHT_KEYS + (this.canUpdate(state) ? 'update' : 'updateHint');
  }

  riskTone(view: CopilotInsightView): string {
    return badgeTone(view.insight?.content.relationship.churnRisk.level ?? '');
  }

  riskKey(view: CopilotInsightView): string {
    return riskKey(SUMMARY_KEYS, view.insight?.content.relationship.churnRisk.level ?? 'low');
  }

  /** Shown only while there are new messages; the update button says so otherwise (RF-30). */
  newMessagesKey(count: number): string {
    return SUMMARY_KEYS + (count === 1 ? 'newMessagesOne' : 'newMessagesMany');
  }

  /** What the line states before the first insight (RF-28). */
  waitingKey(view: CopilotInsightView): string {
    const waiting = view.waiting;
    if (!waiting || waiting.messageCount < waiting.n) return INSIGHT_KEYS + 'waitingForMessages';
    return INSIGHT_KEYS + (waiting.assigned ? 'waitingForNextMessage' : 'waitingForAttendant');
  }

  hasActions(view: CopilotActionsView): boolean {
    return !!view.actions?.items.length;
  }

  pending(view: CopilotActionsView): CopilotActionsItem[] {
    return (view.actions?.items ?? []).filter((item) => item.state === 'pending');
  }

  /** The first pending action is the most important one (RF-15). */
  next(view: CopilotActionsView): CopilotActionsItem | null {
    return this.pending(view)[0] ?? null;
  }

  actionsGenerating(view: CopilotActionsView): boolean {
    return view.state === 'generating' && !this.closed(view);
  }

  /** A failed first generation leaves nothing to show, so the line says so, as the insight's does (RF-07). */
  actionsFailed(view: CopilotActionsView): boolean {
    return view.state === 'failed' && !this.hasActions(view) && !this.closed(view);
  }

  /** Actions never generated, for an insight older than them; the full view offers to generate them (RF-33). */
  actionsNotGenerated(view: CopilotActionsView): boolean {
    return view.canGenerate && !this.closed(view);
  }

  /** The checkbox is offered to the responsible attendant only (RF-35). */
  markable(view: CopilotActionsView): boolean {
    return view.canMark && !this.closed(view);
  }

  isMarking(state: CopilotActionsUiState, item: CopilotActionsItem): boolean {
    return state.marking.includes(item.id);
  }

  countKey(count: number): string {
    if (count === 0) return ACTIONS_KEYS + 'pendingNone';
    return ACTIONS_KEYS + (count === 1 ? 'pendingOne' : 'pendingMany');
  }

  markDone(item: CopilotActionsItem): void {
    this.actionsService.mark(item.id, 'done');
  }

  generate(): void {
    this.insightService.generate();
  }
}
